/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { Link } from 'react-router';

import styled from '@emotion/styled';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography, Typography2 } from '@coursera/cds-core';

import type { FunctionMapping } from 'bundles/enterprise-admin-skills/components/types';
import { formatDepartmentName } from 'bundles/enterprise-admin-skills/components/utils';
import type { SkillsetCardSkillset } from 'bundles/enterprise-admin-skills/types';
import { Card, CardContent, Footer, getGoalType } from 'bundles/enterprise-skillset-bank/components/SkillSetCard';
import { SkillSetCardHeader } from 'bundles/enterprise-skillset-bank/components/SkillSetCardHeader';
import {
  ExtraProficiencies,
  Proficiencies,
  ProficiencyRow,
} from 'bundles/enterprise-skillset-bank/components/SkillSetCardProficiencies';
import { Clickable, Spacer, truncate } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-skills';

const ProgramCount = styled(Typography)({
  whiteSpace: 'nowrap',
  color: '#636363',
});

type Props = {
  skillSet: SkillsetCardSkillset;
  onTitleClick?: () => void;
  linkUrl?: string;
  notification?: React.ReactNode;
  isSelected?: boolean;
  functionMappings?: FunctionMapping[];
  hideLearningPrograms?: boolean;
};

type State = {};

class EnterpriseSkillSetCard extends React.LegacyComponentWithChildren<Props, State> {
  render() {
    const {
      skillSet,
      onTitleClick,
      linkUrl,
      notification,
      children,
      isSelected,
      functionMappings,
      hideLearningPrograms,
    } = this.props;

    const programCount = skillSet.programs?.elements.length ?? 0;
    const proficiencies = skillSet.targetSkillProficiencies ?? [];
    const targets = skillSet.skillProficiencyTargets?.edges ?? [];
    const title = skillSet.title ?? skillSet.name ?? '';

    const titleChildren = (
      <Typography variant="h2semibold" color="body">
        {truncate(title, 65)}
      </Typography>
    );
    let titleNode: React.ReactNode;
    if (linkUrl) {
      titleNode = <Link to={linkUrl}>{titleChildren}</Link>;
    } else if (onTitleClick) {
      titleNode = (
        <Clickable onClick={onTitleClick} data-e2e={`skillset-card-title-${skillSet.id}`}>
          {titleChildren}
        </Clickable>
      );
    } else {
      titleNode = titleChildren;
    }
    const skillsLimit = 6;
    return (
      <Card data-e2e="skillset-card" isSelected={isSelected}>
        {notification && (
          <div>
            {notification}
            <Spacer size={12} />
          </div>
        )}
        <CardContent>
          {titleNode}
          <Spacer size={12} />
          <div css={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start' }}>
            <Typography2 component="p" color="supportText">
              {[
                skillSet.function?.name ?? formatDepartmentName(skillSet.functionId, functionMappings),
                getGoalType(skillSet.goalType),
              ]
                .filter((_) => _)
                .join(' - ')}
            </Typography2>
            <Spacer size={24} />
            {Boolean(programCount) && !hideLearningPrograms && (
              <ProgramCount>
                <FormattedMessage
                  message={_t('Added to {number} learning {number, plural, =1 {program} other {programs}}')}
                  number={programCount?.toString()}
                />
              </ProgramCount>
            )}
          </div>
          <Spacer size={12} />
          <SkillSetCardHeader skillCount={proficiencies.length || targets.length} />
          <Proficiencies>
            {proficiencies.slice(0, skillsLimit).map((proficiency, index) => {
              return (
                <ProficiencyRow
                  key={`${proficiency.skillId}-${String(index)}`}
                  name={proficiency.skillName ?? ''}
                  target={proficiency.targetProficiency ?? 0}
                />
              );
            })}
            {proficiencies.length > skillsLimit && (
              <ExtraProficiencies extraCount={proficiencies.length - skillsLimit} onViewDetails={onTitleClick} />
            )}
            {targets.slice(0, skillsLimit).map((proficiency, index) => {
              return (
                <ProficiencyRow
                  key={`${proficiency?.node.id}-${String(index)}`}
                  name={proficiency?.node.skillName ?? ''}
                  target={proficiency?.node.targetProficiencyScore ?? 0}
                />
              );
            })}
            {targets.length > skillsLimit && (
              <ExtraProficiencies extraCount={targets.length - skillsLimit} onViewDetails={onTitleClick} />
            )}
          </Proficiencies>
          {children && <Footer>{children}</Footer>}
        </CardContent>
      </Card>
    );
  }
}

export default EnterpriseSkillSetCard;
